/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnChanges,
  Output,
  Renderer2,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigService, ScreenSize, ScreenSizeService } from '@libs/shared-services';
import {
  addGumCoordinates,
  asset,
  CROSS_OPACITY,
  drawGumConnector,
  drawGumGradientsByCoordinates,
  drawGumPeriosByCoordinates,
  drawSelectionCross,
  drawTeeth,
  drawTeethNumbers,
  FurcationRef,
  getPerioViewBoxMatrix,
  GUM_INTERMEDIATE_BUMP,
  isReverse,
  Jaw,
  MeasurementCoordinates,
  MobilityRef,
  PERIO_BASE_CONTAINER_WIDTH,
  PERIO_CONTAINER_HEIGHT,
  PERIO_CONTAINER_WIDTH,
  PERIO_MISSING_TEETH_CONTAINER_WIDTH,
  PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
  PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL_FORM_VIEW,
  PERIO_TEETH_CONTAINER_PADDING_X,
  perioViewBoxMatrix,
  PocketDepthRef,
  Quadrant,
  Svg,
  SvgCircle,
  SvgPath,
  SvgRect,
  SvgText,
  Tooth,
  TOOTH_PERIO_HEIGHT,
  TOOTH_WIDTH,
  ToothSide,
  ToothWithLayers,
} from '@libs/ui-lib';
import { fadeInOnly, fadeInOut } from 'libs/perio/src/lib/common/animations';
import { select, selectAll } from 'd3-selection';
import 'd3-transition';
import { SelectedCompare } from '../perio-screen';
import { PerioCompareOverlayComponent } from '../perio-compare-overlay/perio-compare-overlay.component';
import {
  GUM_INTERMEDIARY_BUMP,
  PERIO_MISSING_TEETH_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
  PERIO_TOP_PADDING,
  TEETH_TRANSLATE_Y,
} from './teeth.constants';
import { calculateCoordinates } from './coordinates';
import { drawBackgroundLines, drawCross, drawInsideLabels, drawJawPlacementText, drawPerioTreatments, drawSideLabels } from './drawing';

export const ANIMATION_DURATION = 700;
const SINGLE_VIEW_CONTENT_WIDTH = 140; // Width of the full content area in the SVG viewBox
const SINGLE_VIEW_CONTENT_HEIGHT = 200; // Height of the full content area in the SVG viewBox
const SIDE_LABEL_WIDTH = 30; // Width of the side labels in the SVG viewBox

@Component({
  selector: 'lib-teeth',
  standalone: true,
  imports: [CommonModule, PerioCompareOverlayComponent],
  templateUrl: './teeth.component.html',
  styleUrl: './teeth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut, fadeInOnly],
})
export class TeethComponent implements OnChanges, AfterViewInit {
  private screenSize: ScreenSize = 'xl';
  protected SelectedCompare = SelectedCompare;
  protected Quadrant = Quadrant;

  private readonly gmLinePathRefs: SvgPath[] = [];
  private readonly jawLocationTextRefs: SvgText[] = [];
  private readonly sideLabelRefs: (SvgRect | SvgText)[] = [];
  private readonly insideLabelRefs: (SvgRect | SvgText)[] = [];
  private readonly crossPathRefs: SvgPath[] = [];
  private readonly pocketDepthRefs: PocketDepthRef[] = [];
  private readonly bleedingAndSuppurationRefs: SvgCircle[] = [];
  private readonly mobilityRefs: MobilityRef[] = [];
  private readonly furcationRefs: FurcationRef[] = [];
  private readonly mainSvgRef = signal<Svg | null>(null);

  @Output() public clicked = new EventEmitter<number>();

  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly configService: ConfigService = inject(ConfigService);
  public teethAssetBasePath = this.configService.config()?.teethAssetBasePath ?? '';

  public readonly chart = viewChild<ElementRef>('chart');
  public readonly leftTexts = viewChild<ElementRef>('leftTexts');

  public readonly currentTeeth = input.required<ToothWithLayers[]>();
  public readonly previousTeeth = input<ToothWithLayers[] | undefined>();
  public readonly setCompareSelected = input<SelectedCompare | null>(null);
  public readonly singleTooth = input<ToothWithLayers | undefined>();
  public readonly missingTeeth = input<number[] | undefined>();
  public readonly extractedTeeth = input<number[] | undefined>();
  public readonly singleToothViewContentWidth = input<number>(SINGLE_VIEW_CONTENT_WIDTH);
  public readonly singleToothViewContentHeight = input<number>(SINGLE_VIEW_CONTENT_HEIGHT);
  public readonly isSingleToothView = input(false);
  public readonly isPerioHistoryView = input(false);
  public readonly isPerioExamView = input(false);
  public readonly drawLabels = input(true);
  public readonly drawNumbers = input(true);
  public readonly drawCross = input(true);
  public readonly drawBackgroundLines = input(true);
  public readonly missingTeethView = input(false);
  public readonly drawPerio = input(true);
  public readonly activeSide = input<ToothSide>();
  public readonly isAnimated = input(true);

  public currentTopRightTeeth = signal<Tooth[]>([]);
  public currentTopLeftTeeth = signal<Tooth[]>([]);
  public currentBottomLeftTeeth = signal<Tooth[]>([]);
  public currentBottomRightTeeth = signal<Tooth[]>([]);
  public currentSelectedQuadrantTeeth = signal<Tooth[]>([]);

  public previousTopRightTeeth = signal<Tooth[]>([]);
  public previousTopLeftTeeth = signal<Tooth[]>([]);
  public previousBottomLeftTeeth = signal<Tooth[]>([]);
  public previousBottomRightTeeth = signal<Tooth[]>([]);
  public previousSelectedQuadrantTeeth = signal<Tooth[]>([]);

  public selectedQuadrant = signal<Quadrant | null>(null);
  private readonly perioViewBox = signal<string | null>(null);
  protected compareSelected = signal<SelectedCompare | null>(null);
  protected perioViewBoxMatrix = signal(perioViewBoxMatrix);
  protected containerHeight = computed(() =>
    PERIO_CONTAINER_HEIGHT(
      this.screenSize,
      this.isSingleToothView() ? PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL_FORM_VIEW : PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
      this.missingTeethView() ? 0 : undefined,
    ),
  );

  constructor(protected readonly renderer: Renderer2) {
    effect(() => {
      const value = this.setCompareSelected();
      this.compareSelected.set(value);

      this.updateSvgVisibilityWithCompare({ isAnimated: this.isAnimated() });
      this.updateSvgVisibility({ isAnimated: this.isAnimated() });
    });

    effect(() => this.updateCurrentTeethSignals());
    effect(() => this.updatePreviousTeethSignals());
    effect(() => {
      if (this.missingTeeth()) {
        this.draw();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const currentTeeth = this.currentTeeth();
    const singleTooth = this.singleTooth();

    if (changes['singleTooth'] && singleTooth && this.isSingleToothView()) {
      if (this.isPerioExamView()) {
        this.setViewBoxForSingleTooth(this.screenSize, singleTooth);
        this.updatePerioTreatments();
      } else {
        this.updateViewForSingleTooth(singleTooth);
      }
    }

    if (changes['currentTeeth'] && currentTeeth && this.drawLabels() && !this.isSingleToothView()) {
      this.draw();
      this.drawJawLocationTexts();
      this.updateSvgVisibility({ isAnimated: false });
    }

    if (changes['activeSide']) {
      this.draw();
    }
  }

  public ngAfterViewInit(): void {
    this.screenSizeService.screenSize$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((screenSize) => {
      this.screenSize = screenSize;
      const singleTooth = this.singleTooth();

      this.updateSvgVisibilityWithCompare({ isAnimated: false });
      this.updateSvgVisibility({ isAnimated: false });

      if (this.isSingleToothView() && singleTooth) {
        this.updateViewForSingleTooth(singleTooth);
      } else if (this.missingTeethView()) {
        this.setMissingToothViewBox(screenSize);
        this.draw();
      } else {
        this.perioViewBox.set(perioViewBoxMatrix[screenSize]);
        this.draw();
      }
    });
  }

  // This function updates certain parts of the main SVG based on the selected quadrant. For example in quadrant view, we need to hide the cross.
  private updateSvgVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const quadrant = this.selectedQuadrant();
    const isQuadrantViewMode = quadrant !== null;

    selectAll('[hide-in-quadrant]').each(function () {
      const el = select(this);
      el.transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', isQuadrantViewMode ? 0 : 1);
    });

    this.toggleCrossVisibility({ isAnimated });
    this.toggleSideTextsVisibility({ isAnimated });
  }

  // Certain parts of the main SVG needs to be hidden in compare mode.
  // For example in compare mode, we need to hide the pocket depths, because they are shown in the compare overlay.
  private updateSvgVisibilityWithCompare({ isAnimated }: { isAnimated?: boolean } = {}): void {
    this.togglePocketDepthsVisibility({ isAnimated });
    this.toggleMobilityVisibility({ isAnimated });
    this.toggleFurcationVisibility({ isAnimated });
    this.toggleBleedingAndSuppurationVisibility({ isAnimated });
    this.toggleGmLineVisibility({ isAnimated });
  }

  private updateViewForSingleTooth(singleTooth: Tooth): void {
    this.setViewBoxForSingleTooth(this.screenSize, singleTooth);
    this.draw();
    this.togglePocketDepthsVisibility({ isAnimated: false });
    this.positionSideLabelsForSingleTooth();
    if (this.isPerioHistoryView()) {
      this.hidePerioHistoryViewTexts();
    }
  }

  private updateCurrentTeethSignals(): void {
    const currentTeeth = this.currentTeeth();
    this.currentTopRightTeeth.set(currentTeeth.filter((t) => t.jaw === 'upper').slice(0, 8));
    this.currentTopLeftTeeth.set(currentTeeth.filter((t) => t.jaw === 'upper').slice(8, 16));
    this.currentBottomLeftTeeth.set(
      currentTeeth
        .filter((t) => t.jaw === 'lower')
        .slice(0, 8)
        .sort((a, b) => b.position - a.position),
    );
    this.currentBottomRightTeeth.set(
      currentTeeth
        .filter((t) => t.jaw === 'lower')
        .slice(8, 16)
        .sort((a, b) => b.position - a.position),
    );
  }

  private setMissingToothViewBox(screenSize: ScreenSize): void {
    this.perioViewBoxMatrix.set(
      getPerioViewBoxMatrix(PERIO_MISSING_TEETH_SPACE_BETWEEN_FACIAL_AND_LINGUAL, undefined, PERIO_MISSING_TEETH_CONTAINER_WIDTH),
    );
    this.perioViewBox.set(this.perioViewBoxMatrix()[screenSize]);
  }

  private updatePreviousTeethSignals(): void {
    const previousTeeth = this.previousTeeth();
    if (!previousTeeth) return;

    this.previousTopRightTeeth.set(previousTeeth.filter((t) => t.jaw === 'upper').slice(0, 8));
    this.previousTopLeftTeeth.set(previousTeeth.filter((t) => t.jaw === 'upper').slice(8, 16));
    this.previousBottomLeftTeeth.set(
      previousTeeth
        .filter((t) => t.jaw === 'lower')
        .slice(0, 8)
        .sort((a, b) => b.position - a.position),
    );
    this.previousBottomRightTeeth.set(
      previousTeeth
        .filter((t) => t.jaw === 'lower')
        .slice(8, 16)
        .sort((a, b) => b.position - a.position),
    );

    const quadrantMap = {
      [Quadrant.TopRight]: this.previousTopRightTeeth(),
      [Quadrant.TopLeft]: this.previousTopLeftTeeth(),
      [Quadrant.BottomLeft]: this.previousBottomLeftTeeth(),
      [Quadrant.BottomRight]: this.previousBottomRightTeeth(),
    };

    const selectedQuadrant = this.selectedQuadrant();
    this.previousSelectedQuadrantTeeth.set(selectedQuadrant === null ? [] : quadrantMap[selectedQuadrant]);
  }

  // Zoom into the top left quadrant
  public zoomToTopLeft(): void {
    this.selectedQuadrant.set(Quadrant.TopLeft);
    this.currentSelectedQuadrantTeeth.set(this.currentTopLeftTeeth());
    this.previousSelectedQuadrantTeeth.set(this.previousTopLeftTeeth());
    this.zoomToViewBox(`${PERIO_BASE_CONTAINER_WIDTH / 2 - 20} 0 ${PERIO_BASE_CONTAINER_WIDTH / 2} ${this.containerHeight() / 2 + 10}`);
  }

  // Zoom into the top right quadrant
  public zoomToTopRight(): void {
    this.selectedQuadrant.set(Quadrant.TopRight);
    this.currentSelectedQuadrantTeeth.set(this.currentTopRightTeeth());
    this.previousSelectedQuadrantTeeth.set(this.previousTopRightTeeth());
    this.zoomToViewBox(`0 0 ${PERIO_BASE_CONTAINER_WIDTH / 2} ${this.containerHeight() / 2 + 10}`);
  }

  // Zoom into the bottom left corner
  public zoomToBottomLeft(): void {
    this.selectedQuadrant.set(Quadrant.BottomLeft);
    this.currentSelectedQuadrantTeeth.set(this.currentBottomLeftTeeth());
    this.previousSelectedQuadrantTeeth.set(this.previousBottomLeftTeeth());
    this.zoomToViewBox(
      `${PERIO_BASE_CONTAINER_WIDTH / 2 - 20} ${this.containerHeight() / 2} ${PERIO_BASE_CONTAINER_WIDTH / 2} ${
        this.containerHeight() / 2
      }`,
    );
  }

  // Zoom into the bottom right corner
  public zoomToBottomRight(): void {
    this.selectedQuadrant.set(Quadrant.BottomRight);
    this.currentSelectedQuadrantTeeth.set(this.currentBottomRightTeeth());
    this.previousSelectedQuadrantTeeth.set(this.previousBottomRightTeeth());
    this.zoomToViewBox(`0 ${this.containerHeight() / 2} ${PERIO_BASE_CONTAINER_WIDTH / 2} ${this.containerHeight() / 2 + 10}`);
  }

  // Zoom back to center
  public zoomToCenter(): void {
    this.selectedQuadrant.set(null);
    this.currentSelectedQuadrantTeeth.set([]);
    this.previousSelectedQuadrantTeeth.set([]);
    this.zoomToViewBox(perioViewBoxMatrix[this.screenSize]);
  }

  private toggleCrossVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isQuadrantViewMode = this.selectedQuadrant() !== null;
    this.crossPathRefs.forEach((ref) => {
      const el = ref.node();
      if (!el) return;
      if (isQuadrantViewMode) {
        (ref as any)
          .transition()
          .duration(isAnimated ? ANIMATION_DURATION : 0)
          .attr('opacity', 0);
      } else {
        (ref as any)
          .transition()
          .duration(isAnimated ? ANIMATION_DURATION : 0)
          .attr('opacity', CROSS_OPACITY);
      }
    });
  }

  private setViewBoxForSingleTooth(screenSize: ScreenSize, tooth: Tooth) {
    const { upperJawBuccalTeethCoordinates, lowerJawBuccalTeethCoordinates } = calculateCoordinates(
      this.currentTeeth(),
      this.screenSize,
      PERIO_CONTAINER_WIDTH(screenSize),
      TEETH_TRANSLATE_Y(screenSize, PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL_FORM_VIEW),
    );

    const toothPosition =
      tooth.jaw === 'upper' ? upperJawBuccalTeethCoordinates[tooth.position] : lowerJawBuccalTeethCoordinates[tooth.position];

    // Calculate viewBox positioning based on view mode:
    // - Perio History View: tooth positioned 40px from left edge (140/2 - 30 = 40px offset)
    // - Regular Single Tooth View: tooth centered in 110px content area (55px offset from tooth center)
    const toothContentWidth =
      this.isPerioHistoryView() || this.isPerioExamView()
        ? this.singleToothViewContentWidth() / 2 - SIDE_LABEL_WIDTH // 40px - positions tooth at x=40 in viewBox
        : (this.singleToothViewContentWidth() - SIDE_LABEL_WIDTH) / 2; // 55px - centers tooth in 110px content area

    const viewBoxX = toothPosition.x - toothContentWidth;
    this.perioViewBox.set(
      `${viewBoxX} ${tooth.jaw === 'upper' ? 80 : 391} ${this.singleToothViewContentWidth()} ${this.singleToothViewContentHeight()}`,
    );
  }

  private togglePocketDepthsVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isCompareViewMode = this.compareSelected() === SelectedCompare.PocketDepth;

    // eslint-disable-next-line sonarjs/cognitive-complexity
    this.pocketDepthRefs.forEach((ref) => {
      const el = ref.node();
      const opacity = 1;

      if (!el) return;
      if (el instanceof SVGPathElement || el instanceof SVGRectElement) {
        // Animate opacity for path or rect
        ref
          .transition()
          .duration(isAnimated ? ANIMATION_DURATION : 0)
          .attr('opacity', isCompareViewMode ? 0 : opacity);
      } else if (el instanceof SVGTextElement) {
        if (this.selectedQuadrant() === null && this.isSingleToothView() === false) {
          const originalSize = ref.attr('big-font-size');
          const smallFontSize = ref.attr('small-font-size');
          const normalY = ref.attr('y-normal');
          const initialY = ref.attr('y');
          const shouldHide = ref.attr('hide-in-full') === 'true' && this.isSingleToothView() === false;
          (ref as any)
            .transition()
            .duration(isAnimated ? ANIMATION_DURATION : 0)
            .attr('opacity', shouldHide ? 0 : 1)
            .attr('y', Number.parseFloat(ref.text()) === 2 ? initialY : normalY)
            .attr('font-size', Number.parseFloat(ref.text()) === 2 ? smallFontSize : originalSize);
        } else {
          const smallFontSize = ref.attr('small-font-size');
          const extraSmallFontSize = ref.attr('extra-small-font-size');
          const yQuadrant = ref.attr('y-quadrant');
          const shouldHideInPerioHistory = this.isPerioHistoryView() && ref.attr('hide-in-preview') === 'true';
          (ref as any)
            .transition()
            .duration(isAnimated ? ANIMATION_DURATION : 0)
            .attr('opacity', shouldHideInPerioHistory ? 0 : 1)
            .attr('y', yQuadrant)
            .attr('font-size', ref.attr('has-one-value') === 'true' ? extraSmallFontSize : smallFontSize);
        }
      }
    });
  }

  private toggleSideTextsVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const quadrant = this.selectedQuadrant();
    const inQuadrant = quadrant !== null;
    const isTopQuadrant = quadrant === Quadrant.TopLeft || quadrant === Quadrant.TopRight;
    const isLowerQuadrant = quadrant === Quadrant.BottomLeft || quadrant === Quadrant.BottomRight;
    const isCompare = this.compareSelected() === SelectedCompare.PocketDepth;

    this.sideLabelRefs.forEach((ref) => {
      const shouldHide =
        (ref.attr('hide-in-full') === 'true' && !inQuadrant) ||
        (ref.attr('hide-in-top-jaw') === 'true' && isTopQuadrant) ||
        (ref.attr('hide-in-lower-jaw') === 'true' && isLowerQuadrant) ||
        (ref.attr('hide-in-compare') === 'true' && isCompare);

      (ref as any)
        .transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', shouldHide ? 0 : 1);
    });
  }

  private positionSideLabelsForSingleTooth(): void {
    const tooth = this.singleTooth();
    if (!this.isSingleToothView() || !tooth || this.isPerioHistoryView() || this.isPerioExamView()) return;

    // Calculate coordinates for the selected tooth
    const teeth = this.currentTeeth();
    const screenSize = this.screenSize;
    const { upperJawBuccalTeethCoordinates, lowerJawBuccalTeethCoordinates } = calculateCoordinates(
      teeth,
      screenSize,
      PERIO_CONTAINER_WIDTH(screenSize),
      TEETH_TRANSLATE_Y(screenSize, PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL_FORM_VIEW),
    );

    const toothCoords =
      tooth.jaw === 'upper' ? upperJawBuccalTeethCoordinates[tooth.position] : lowerJawBuccalTeethCoordinates[tooth.position];

    // Calculate viewbox left edge using the same logic as setViewBoxForSingleTooth
    // In regular single tooth view: tooth centered in 110px content area (55px offset from tooth center)
    const toothContentWidth = (SINGLE_VIEW_CONTENT_WIDTH - SIDE_LABEL_WIDTH) / 2; // 55px
    const viewBoxLeftEdge = toothCoords.x - toothContentWidth;

    this.sideLabelRefs.forEach((ref) => {
      const originalX = Number.parseFloat(ref.attr('x') || '0');
      const newX = viewBoxLeftEdge + originalX;
      (ref as any).attr('x', newX).attr('opacity', 1);
    });
  }

  private hidePerioHistoryViewTexts(): void {
    [...this.furcationRefs, ...this.mobilityRefs, ...this.pocketDepthRefs].forEach((ref) => {
      if (ref.attr('hide-in-preview') === 'true') {
        (ref as any).attr('opacity', 0);
      }
    });
  }

  private toggleGmLineVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isCompareViewMode = this.compareSelected() === SelectedCompare.PocketDepth;
    const singleTooth = this.singleTooth();
    const isPerioExamView = this.isPerioExamView();
    const activeSide = this.activeSide();

    this.gmLinePathRefs.forEach((ref) => {
      const el = ref.node();
      let opacity = 1;
      if (singleTooth && isPerioExamView) {
        opacity = activeSide === el?.getAttribute('data-side') ? 1 : 0.5;
      }
      (ref as any)
        .transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', isCompareViewMode ? 0 : opacity);
    });
  }

  private toggleBleedingAndSuppurationVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isCompareViewMode = this.compareSelected() === SelectedCompare.PocketDepth;
    const singleTooth = this.singleTooth();
    const isPerioExamView = this.isPerioExamView();
    const activeSide = this.activeSide();

    this.bleedingAndSuppurationRefs.forEach((ref) => {
      const el = ref.node();
      let opacity = 1;
      if (singleTooth && isPerioExamView) {
        opacity = activeSide === el?.getAttribute('data-side') ? 1 : 0.5;
      }
      (ref as any)
        .transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', isCompareViewMode ? 0 : opacity);
    });
  }

  private toggleMobilityVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isCompareViewMode = this.compareSelected() === SelectedCompare.PocketDepth;
    this.mobilityRefs.forEach((mobility) => {
      (mobility as any)
        .transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', isCompareViewMode ? 0 : 1);
    });
  }

  private toggleFurcationVisibility({ isAnimated = true }: { isAnimated?: boolean } = {}): void {
    const isCompareViewMode = this.compareSelected() === SelectedCompare.PocketDepth;

    this.furcationRefs.forEach((ref) => {
      (ref as any)
        .transition()
        .duration(isAnimated ? ANIMATION_DURATION : 0)
        .attr('opacity', isCompareViewMode ? 0 : 1);
    });
  }

  // Helper method to change the viewBox with animation
  private zoomToViewBox(viewBox: string): void {
    const svg = select(this.chart()?.nativeElement).select('svg');
    this.perioViewBox.set(viewBox);
    svg.transition().duration(ANIMATION_DURATION).attr('viewBox', this.perioViewBox());
  }

  private drawJawLocationTexts(): void {
    const leftTexts = this.leftTexts();
    if (!leftTexts) return;
    select(leftTexts.nativeElement).select('svg').remove();

    const svg = select(leftTexts?.nativeElement)
      .append('svg')
      .attr('viewBox', '0 0 50 750')
      .style('width', '100%')
      .style('height', '100%');

    this.jawLocationTextRefs.push(
      ...drawJawPlacementText(svg, PERIO_CONTAINER_HEIGHT(this.screenSize), PERIO_CONTAINER_WIDTH(this.screenSize), 30, 30),
    );
  }

  private updatePerioTreatments() {
    const svg = this.mainSvgRef();

    if (!svg) {
      return;
    }

    svg.attr('viewBox', this.perioViewBox());

    const coords = calculateCoordinates(
      this.currentTeeth(),
      this.screenSize,
      PERIO_CONTAINER_WIDTH(this.screenSize, this.missingTeethView() ? PERIO_MISSING_TEETH_CONTAINER_WIDTH : PERIO_BASE_CONTAINER_WIDTH),
      TEETH_TRANSLATE_Y(this.screenSize, PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL),
    );

    const [pocketDepthRefs] = drawPerioTreatments(
      svg,
      coords.upperJawTeeth,
      coords.lowerJawTeeth,
      true,
      coords.upperJawBuccalPeriosCoordinates,
      coords.upperJawLingualPeriosCoordinates,
      coords.lowerJawLingualPeriosCoordinates,
      coords.lowerJawBuccalPeriosCoordinates,
      coords.upperJawBuccalTeethCoordinates,
      coords.lowerJawBuccalTeethCoordinates,
      this.activeSide(),
    );
    this.pocketDepthRefs.push(...pocketDepthRefs);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private draw(): void {
    const teeth = this.currentTeeth();
    const chart = this.chart();
    if (!chart || !teeth?.length) return;
    select(chart.nativeElement).select('svg').remove();

    const svg = select(chart.nativeElement)
      .append('svg')
      .attr('viewBox', this.perioViewBox())
      .style('width', '100%')
      .style('height', '100%');

    this.mainSvgRef.set(svg);

    const distanceBetweenFacialAndLingual = this.isSingleToothView()
      ? PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL_FORM_VIEW
      : PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL;

    const {
      upperJawTeeth,
      lowerJawTeeth,
      upperJawBuccalTeethCoordinates,
      upperJawLingualTeethCoordinates,
      lowerJawLingualTeethCoordinates,
      lowerJawBuccalTeethCoordinates,

      upperJawBuccalPeriosCoordinates,
      upperJawLingualPeriosCoordinates,
      lowerJawBuccalPeriosCoordinates,
      lowerJawLingualPeriosCoordinates,
    } = calculateCoordinates(
      teeth,
      this.screenSize,
      PERIO_CONTAINER_WIDTH(this.screenSize, this.missingTeethView() ? PERIO_MISSING_TEETH_CONTAINER_WIDTH : PERIO_BASE_CONTAINER_WIDTH),
      TEETH_TRANSLATE_Y(
        this.screenSize,
        this.missingTeethView() ? PERIO_MISSING_TEETH_SPACE_BETWEEN_FACIAL_AND_LINGUAL : distanceBetweenFacialAndLingual,
      ),
      this.missingTeethView() ? GUM_INTERMEDIARY_BUMP(this.screenSize) : undefined,
    );

    // 1. Draw the gums if needed
    if (this.missingTeethView()) {
      const coordinateListRecord: Record<ToothSide, Record<Jaw, MeasurementCoordinates[]>> = {
        buccal: {
          upper: upperJawBuccalPeriosCoordinates,
          lower: lowerJawBuccalPeriosCoordinates,
        },
        lingual: {
          upper: upperJawLingualPeriosCoordinates,
          lower: lowerJawLingualPeriosCoordinates,
        },
      };

      const getGinguvalMarginCoords = (side: ToothSide, jaw: Jaw) => coordinateListRecord[side][jaw].map((t) => t.gingivalMargin);

      const intermediateBump = GUM_INTERMEDIATE_BUMP(this.screenSize);
      const containerWidth = PERIO_CONTAINER_WIDTH(
        this.screenSize,
        this.missingTeethView() ? PERIO_MISSING_TEETH_CONTAINER_WIDTH : PERIO_BASE_CONTAINER_WIDTH,
      );
      const getYCoordinates = (side: ToothSide, jaw: Jaw, upperJawToothPos: number) =>
        coordinateListRecord[side][jaw][upperJawToothPos].base?.y ?? 0;
      const perioHeight = TOOTH_PERIO_HEIGHT(this.screenSize);
      const xPadding = PERIO_TEETH_CONTAINER_PADDING_X(this.screenSize);

      const getAddGumCoordinatesParamList = (
        side: ToothSide,
        jaw: Jaw,
        firstUpperJawToothPosition: number,
      ): Parameters<typeof addGumCoordinates> => [
        getGinguvalMarginCoords(side, jaw),
        jaw,
        intermediateBump,
        containerWidth,
        PERIO_TOP_PADDING,
        xPadding,
        getYCoordinates(side, jaw, firstUpperJawToothPosition),
        perioHeight,
        side === 'buccal' ? -5 : 5,
        side,
      ];

      const extendedUpperBuccalGumCoordinates = addGumCoordinates(
        ...getAddGumCoordinatesParamList('buccal', 'upper', upperJawTeeth[0].position),
      );

      const extendedUpperLingualGumCoordinates = addGumCoordinates(
        ...getAddGumCoordinatesParamList('lingual', 'upper', upperJawTeeth[0].position),
      );

      const extendedLowerBuccalGumCoordinates = addGumCoordinates(
        ...getAddGumCoordinatesParamList('buccal', 'lower', lowerJawTeeth[0].position),
      );

      const extendedLowerLingualGumCoordinates = addGumCoordinates(
        ...getAddGumCoordinatesParamList('lingual', 'lower', lowerJawTeeth[0].position),
      );

      drawGumPeriosByCoordinates(svg, extendedUpperBuccalGumCoordinates, 0.08);
      drawGumPeriosByCoordinates(svg, extendedLowerBuccalGumCoordinates, 0.08);
      drawGumPeriosByCoordinates(svg, extendedUpperLingualGumCoordinates, 0.08);
      drawGumPeriosByCoordinates(svg, extendedLowerLingualGumCoordinates, 0.08);

      const firstUpperLingualTooth = upperJawLingualTeethCoordinates[upperJawTeeth[0].position];
      const lastLowerLingualTooth = lowerJawLingualTeethCoordinates[lowerJawTeeth.at(-1)!.position];

      // Create a rectangle to connect the lingual gums from first upper tooth to last lower tooth
      drawGumConnector(
        svg,
        firstUpperLingualTooth,
        lastLowerLingualTooth,
        PERIO_CONTAINER_WIDTH(this.screenSize, PERIO_MISSING_TEETH_CONTAINER_WIDTH),
        TOOTH_WIDTH(this.screenSize),
        TOOTH_PERIO_HEIGHT(this.screenSize),
        0.08,
      );

      drawGumGradientsByCoordinates(
        svg,
        extendedUpperBuccalGumCoordinates.at(0)!,
        extendedLowerBuccalGumCoordinates.at(0)!,
        extendedUpperBuccalGumCoordinates.at(-1)!,
        extendedLowerBuccalGumCoordinates.at(-1)!,
      );
    }

    // 2. based on tooth coordinates, draw the background grey lines
    if (!this.isPerioHistoryView() && !this.isPerioExamView() && this.drawBackgroundLines()) {
      drawBackgroundLines(svg, upperJawBuccalTeethCoordinates, 'lower');
      drawBackgroundLines(svg, upperJawLingualTeethCoordinates, 'lower');
      drawBackgroundLines(svg, lowerJawLingualTeethCoordinates, 'upper');
      drawBackgroundLines(svg, lowerJawBuccalTeethCoordinates, 'upper');
    }

    // 3. based on teeth coordinates, draw teeth on top of that
    const toothAssetPathTop = (position: number) => `${asset(position)}/base/perio-top.png`;
    const toothAssetPathBottom = (position: number) => `${asset(position)}/base/perio-bottom.png`;

    const allMissingTeeth = new Set([...(this.missingTeeth() || []), ...(this.extractedTeeth() || [])]);
    const filterTeeth = (tooth: ToothWithLayers) => !allMissingTeeth.has(tooth.position);

    const buccalOpacity = this.activeSide() === 'lingual' ? 0.5 : 1;
    const lingualOpacity = this.activeSide() === 'buccal' ? 0.5 : 1;

    drawTeeth({
      svg,
      teeth: upperJawTeeth,
      teethCoordinates: upperJawBuccalTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: false,
      height: TOOTH_PERIO_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1)'),
      toothAssetPath: toothAssetPathTop,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
      },
      shouldDrawTooth: filterTeeth,
      opacity: buccalOpacity,
    });

    drawTeeth({
      svg,
      teeth: upperJawTeeth,
      teethCoordinates: upperJawLingualTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: false,
      height: TOOTH_PERIO_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, -1)' : 'scale(1, -1)'),
      toothAssetPath: toothAssetPathBottom,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
      },
      shouldDrawTooth: filterTeeth,
      opacity: lingualOpacity,
    });

    drawTeeth({
      svg,
      teeth: lowerJawTeeth,
      teethCoordinates: lowerJawLingualTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: false,
      height: TOOTH_PERIO_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, -1)' : 'scale(1, -1)'),
      toothAssetPath: toothAssetPathTop,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
      },
      shouldDrawTooth: filterTeeth,
      opacity: lingualOpacity,
    });

    drawTeeth({
      svg,
      teeth: lowerJawTeeth,
      teethCoordinates: lowerJawBuccalTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: false,
      height: TOOTH_PERIO_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1, 1)'),
      toothAssetPath: toothAssetPathBottom,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
      },
      shouldDrawTooth: filterTeeth,
      opacity: buccalOpacity,
    });

    //3. draw missing teeth crosses if needed
    if (this.missingTeethView() && this.missingTeeth()) {
      drawSelectionCross(
        svg,
        upperJawBuccalTeethCoordinates,
        lowerJawLingualTeethCoordinates,
        this.missingTeeth()!,
        this.extractedTeeth()!,
        TOOTH_WIDTH(this.screenSize),
        TOOTH_PERIO_HEIGHT(this.screenSize) * 2 + 10,
      );
    }

    //4. draw recession lines (or gingival margin (GM) line)
    //if there is no measurements for the buccal upper jaw, we don't draw the recession line
    const hasPerioMeasurements = teeth.some(
      (t) =>
        t.perio.buccal.pocketDepth.distal !== 0 ||
        t.perio.buccal.pocketDepth.central !== 0 ||
        t.perio.buccal.pocketDepth.mesial !== 0 ||
        t.perio.lingual.pocketDepth.distal !== 0 ||
        t.perio.lingual.pocketDepth.central !== 0 ||
        t.perio.lingual.pocketDepth.mesial !== 0,
    );

    // 5. draw cross
    if (this.drawCross()) {
      this.crossPathRefs.push(
        ...drawCross(
          svg,
          this.screenSize,
          this.containerHeight(),
          PERIO_CONTAINER_WIDTH(
            this.screenSize,
            this.missingTeethView() ? PERIO_MISSING_TEETH_CONTAINER_WIDTH : PERIO_BASE_CONTAINER_WIDTH,
          ),
        ),
      );
    }

    // 6. draw teeth numbers
    if (this.drawNumbers()) {
      drawTeethNumbers(svg, upperJawBuccalTeethCoordinates, 'upper', this.screenSize, this.containerHeight(), 17);
      drawTeethNumbers(svg, lowerJawBuccalTeethCoordinates, 'lower', this.screenSize, this.containerHeight(), 17);
    }

    //7. draw suppuration/bleeding on probing circles
    if (this.drawPerio()) {
      const [pocketDepthRefs, mobilityRefs, furcationRefs, bleedingAndSuppurationRefs, gmLinePathRefs] = drawPerioTreatments(
        svg,
        upperJawTeeth,
        lowerJawTeeth,
        hasPerioMeasurements,
        upperJawBuccalPeriosCoordinates,
        upperJawLingualPeriosCoordinates,
        lowerJawLingualPeriosCoordinates,
        lowerJawBuccalPeriosCoordinates,
        upperJawBuccalTeethCoordinates,
        lowerJawBuccalTeethCoordinates,
        this.activeSide(),
      );

      this.pocketDepthRefs.push(...pocketDepthRefs);
      this.mobilityRefs.push(...mobilityRefs);
      this.furcationRefs.push(...furcationRefs);
      this.bleedingAndSuppurationRefs.push(...bleedingAndSuppurationRefs);
      this.gmLinePathRefs.push(...gmLinePathRefs);
    }

    //10. draw the labels (CEJ, 5, 10, Mobility, Furcation)

    if (this.drawLabels()) {
      this.sideLabelRefs.push(...drawSideLabels(svg, PERIO_CONTAINER_HEIGHT(this.screenSize)));
      this.insideLabelRefs.push(...drawInsideLabels(svg));
    }
  }
}
